/**
 * DriverCard — one driver's card in the dispatch-board grid (Wave 3 Part 2).
 *
 * A dnd-kit drop target (id `driver:<driverId>`): dropping a job here assigns it to
 * the driver (appended to the end). Header shows the driver name + a job-count badge;
 * the in-progress job is pinned + highlighted; queued jobs are draggable below it.
 *
 * Quick 260616-f4r:
 *   #3 — availability dot + left-border accent (available=green, busy=amber,
 *        offline=red).
 *   #6 — fixed card height with the job list scrolling internally, so the grid
 *        stays consistent regardless of job count.
 *
 * Task #32 — job rows use JobChip's `driver-card` variant (status/urgent icons, pickup
 * only, job type + real status on hover), and OFF SHIFT is a gray icon.
 */

import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { PowerOff } from 'lucide-react';
import type { DispatchJob } from '../jobs/use-jobs';
import type { DriverSummary } from '../jobs/use-drivers';
import { JobChip } from './JobChip';
import { FadeScroll } from '../../components/FadeScroll';
import { sortDriverJobs, type DriverAvailability } from './board-utils';

export interface DriverCardProps {
  readonly driver: DriverSummary;
  readonly jobs: readonly DispatchJob[];
  readonly availability: DriverAvailability;
  /** Quick 260616-ggl (#4) — hover tooltip on the availability dot (last sync time). */
  readonly syncTitle?: string | undefined;
  readonly onSelect: (jobId: string) => void;
}

const AVAILABILITY: Record<DriverAvailability, { color: string; label: string }> = {
  available: { color: 'var(--color-success)', label: 'Available' },
  busy: { color: 'var(--color-warning)', label: 'Busy' },
  offline: { color: 'var(--color-danger)', label: 'Offline' },
};

/**
 * Fixed card height; the job list scrolls inside (Wave 3 #6 / Quick 260616-fxg #1).
 * Quick 260616-rsz (#2) — 360→280 so more driver cards fit on screen at once;
 * the compact single-line job chips still scroll within the card behind the
 * FadeScroll bottom gradient.
 */
const CARD_HEIGHT = 280;

export function DriverCard({
  driver,
  jobs,
  availability,
  syncTitle,
  onSelect,
}: DriverCardProps): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: `driver:${driver.driverId}`,
    data: { driverId: driver.driverId },
  });
  const ordered = sortDriverJobs(jobs);
  const status = AVAILABILITY[availability];

  // Quick 260616-bdr (#1) — use the longhand top/right/bottom borders, NOT the
  // `border` shorthand, alongside the colored `borderLeft`. Mixing the `border`
  // shorthand with `borderLeft` in one inline-style object lets React's partial
  // style updates re-apply the shorthand on re-render without re-applying the
  // unchanged borderLeft, which silently clobbered the availability color.
  const sideBorder = isOver
    ? '2px solid var(--color-accent-blue)'
    : '1px solid var(--color-border-primary)';

  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '10px',
        height: `${CARD_HEIGHT.toString()}px`,
        boxSizing: 'border-box',
        borderRadius: 'var(--radius-card)',
        borderTop: sideBorder,
        borderRight: sideBorder,
        borderBottom: sideBorder,
        // Availability accent — green (available) / amber (busy) / red (offline).
        borderLeft: `4px solid ${status.color}`,
        backgroundColor: 'var(--color-bg-secondary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          flexShrink: 0,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span
            aria-label={status.label}
            title={syncTitle ?? status.label}
            style={{
              width: '9px',
              height: '9px',
              borderRadius: '50%',
              backgroundColor: status.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '14px',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {driver.driverName}
          </span>
          {/* OPEN-027 prong 3 — an explicit OFF SHIFT marker. Rendered only when the
              API actually said so (`hasOpenShift === false`): an older payload that
              omits the field is UNKNOWN, and silently labelling those drivers "off
              shift" would be worse than the ambiguity this replaces. Drivers who ARE on
              shift get no marker — the overwhelmingly common state, and a badge on
              every card would train dispatchers to stop seeing it, which is exactly
              what costs us the one case that matters. */}
          {/* Task #32 — a small gray icon, not the word. The board hides off-shift
              drivers by default, so a card that shows one is either the viewer's choice
              or a driver who still has jobs on it; the icon says which state it is. */}
          {driver.hasOpenShift === false && (
            <span
              data-testid="off-shift-icon"
              role="img"
              aria-label="Off shift"
              title="Off shift"
              style={{
                display: 'inline-flex',
                flexShrink: 0,
                color: 'var(--color-text-tertiary)',
              }}
            >
              <PowerOff size={13} aria-hidden="true" />
            </span>
          )}
          {/* OPEN-027 follow-up — on shift but no truck. A warning, not information:
              every ticket this driver captures is stamped with a null truck. Requires
              the API to have said both things; an absent field is unknown, as above. */}
          {driver.hasOpenShift === true &&
            (driver.truckNumber === null || driver.truckNumber === '') && (
              <span
                title="On shift with no truck selected — tickets will have no truck"
                style={{
                  flexShrink: 0,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-warning)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-warning)',
                  borderRadius: '4px',
                  padding: '1px 5px',
                }}
              >
                NO TRUCK
              </span>
            )}
          {/* #5 — current truck unit number (open shift), shown when assigned. */}
          {driver.truckNumber != null && driver.truckNumber !== '' && (
            <span
              title={`Truck ${driver.truckNumber}`}
              style={{
                flexShrink: 0,
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                color: 'var(--color-text-secondary)',
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '10px',
                padding: '1px 5px',
              }}
            >
              {driver.truckNumber}
            </span>
          )}
          {/* OPEN-027 follow-up — on shift with no trailer. Neutral, not a warning:
              running bobtail is a legitimate, explicit choice at shift start. Only when
              the API sent `trailerNumber: null`; an older payload omits it (unknown). */}
          {driver.hasOpenShift === true &&
            (driver.trailerNumber === null || driver.trailerNumber === '') && (
              <span
                title="On shift with no trailer (bobtail)"
                style={{
                  flexShrink: 0,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-text-tertiary)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '4px',
                  padding: '1px 5px',
                }}
              >
                NO TRAILER
              </span>
            )}
        </span>
        <span
          aria-label={`${ordered.length.toString()} jobs`}
          style={{
            minWidth: '22px',
            height: '22px',
            padding: '0 6px',
            borderRadius: '11px',
            backgroundColor:
              ordered.length > 0 ? 'var(--color-accent-blue)' : 'var(--color-bg-tertiary)',
            color: ordered.length > 0 ? 'var(--color-on-accent)' : 'var(--color-text-tertiary)',
            fontSize: '12px',
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {ordered.length}
        </span>
      </div>
      {/* #6 — scrollable job list fills the remaining fixed height. #1 (260616-ggl) —
          no visible scrollbar; a fade-to-bg gradient marks more jobs below. */}
      <FadeScroll style={{ flex: 1 }} fadeColor="var(--color-bg-secondary)">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {ordered.length === 0 ? (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Drop a job here
            </span>
          ) : (
            ordered.map((job) => (
              <JobChip key={job.id} job={job} draggable onSelect={onSelect} variant="driver-card" />
            ))
          )}
        </div>
      </FadeScroll>
    </div>
  );
}
